import { useEffect, useRef, useState, type ReactElement } from 'react';
import { clipFinden, hinweisZiel, vorZeit } from '@schnitt/shared';
import { api, getShareToken, type AbnahmeView, type KommentarView } from '../api';
import { useEditor } from '../store';
import { useZeitText } from '../zeit';
import { Icon } from './Icon';
import { Sheet } from './Sheet';

/**
 * Hinweise am Zeitpunkt.
 *
 * Ein Kommentar hängt an einer Sekunde im Film, nicht am Projekt: „hier ist
 * der Schnitt zu hart“ meint eine Stelle. Deshalb steht vor jedem der
 * Zeitcode, und ein Tipp darauf springt hin — das ist der eigentliche Zweck
 * der ganzen Liste.
 *
 * Wer über einen Gast-Link liest, darf schreiben: dafür ist der Link da. Nur
 * abhaken kann er nichts, auch nicht das Eigene — eine Abnahme, die sich
 * selbst abnickt, wäre keine.
 */
/**
 * Der Name, unter dem ein Gast ohne Namen am Link schreibt -- gemerkt, damit
 * er ihn nicht bei jedem Öffnen neu tippt (7.277). Nur eine Bequemlichkeit:
 * fehlt der Speicher, fragt das Feld eben wieder.
 */
const NAME_SCHLUESSEL = 'schnitt.gastname';
function gemerkterName(): string {
  try {
    return localStorage.getItem(NAME_SCHLUESSEL) ?? '';
  } catch {
    return '';
  }
}
function nameMerken(name: string): void {
  try {
    if (name) localStorage.setItem(NAME_SCHLUESSEL, name);
  } catch {
    /* privates Fenster: dann eben nicht */
  }
}

export function KommentarSheet({ onClose, fokus = null }: { onClose: () => void; fokus?: string | null }): ReactElement {
  const zeit = useZeitText();
  const project = useEditor((s) => s.project);
  const kommentare = useEditor((s) => s.kommentare);
  const playhead = useEditor((s) => s.playhead);
  const setPlayhead = useEditor((s) => s.setPlayhead);
  const refresh = useEditor((s) => s.refreshKommentare);
  const kommentareFehler = useEditor((s) => s.kommentareFehler);
  const timeline = useEditor((s) => s.timeline);
  const select = useEditor((s) => s.select);
  // Woran der nächste Hinweis kleben soll (Sascha: „Auch am Objekt
  // entstehen“). Gesetzt hat ihn das Kontextmenü am Clip; wer das Blatt so
  // öffnet, schreibt an diesen Clip, und ein Knopf löst ihn wieder.
  const hinweisAnker = useEditor((s) => s.hinweisAnker);
  const setHinweisAnker = useEditor((s) => s.setHinweisAnker);
  const toast = useEditor((s) => s.toast);
  const role = useEditor((s) => s.role);
  const gastName = useEditor((s) => s.gastName);
  const fps = useEditor((s) => s.timeline.settings.fps);

  const [text, setText] = useState('');
  const [name, setName] = useState(gemerkterName);
  // Nach dem Namen fragt das Blatt jeden Gast, an dessen Link keiner steht --
  // auch einen, der mitschneiden darf (vorher nur „nur ansehen“, und wer
  // mitschnitt, hiess „Gast“). Wer ein Konto hat, schreibt unter seinem.
  const fragtNachNamen = !gastName && getShareToken() !== null;
  const [antwortAuf, setAntwortAuf] = useState<KommentarView | null>(null);
  // Der eigene Hinweis, der gerade geändert wird (7.275). Geändert wird im
  // selben Feld, in dem man schreibt -- ein zweites Feld im Eintrag wäre am
  // Telefon ein zweites Tippen ins Ungewisse.
  const [bearbeite, setBearbeite] = useState<KommentarView | null>(null);
  const [zeigeErledigte, setZeigeErledigte] = useState(false);
  const [busy, setBusy] = useState(false);
  const [abnahmen, setAbnahmen] = useState<AbnahmeView[]>([]);
  /**
   * Ob die Abnahmen schon da sind.
   *
   * Ohne das stand fuer einen Augenblick „Noch nicht freigegeben“ unter einem
   * Schnitt, der laengst freigegeben war -- und darunter der Knopf zum
   * Freigeben, der dann unter dem Finger wegsprang. Der Telefon-Durchgang ist
   * genau darueber zweimal gestolpert; wer das Blatt oeffnet, waere es auch.
   */
  const [abnahmenDa, setAbnahmenDa] = useState(false);
  const [freigabeNotiz, setFreigabeNotiz] = useState('');
  const rev = useEditor((s) => s.rev);

  // Die Abnahmen kommen beim Öffnen frisch: sie ändern sich selten, und eine
  // Liste, die man selten sieht, muss nicht über die Verbindung mitlaufen.
  useEffect(() => {
    if (!project) return;
    let weg = false;
    void api
      .abnahmen(project.id)
      .then(({ abnahmen: liste }) => {
        if (!weg) setAbnahmen(liste);
      })
      .catch(() => undefined)
      .finally(() => {
        // Auch nach einem Fehlschlag: sonst dreht sich das Blatt fuer immer.
        if (!weg) setAbnahmenDa(true);
      });
    return () => {
      weg = true;
    };
  }, [project]);

  const gueltig = abnahmen.find((a) => !a.withdrawn_at) ?? null;
  const seither = gueltig ? rev - gueltig.rev : 0;

  const freigeben = async () => {
    if (!project) return;
    setBusy(true);
    try {
      const { abnahme } = await api.freigeben(project.id, { note: freigabeNotiz.trim(), name });
      if (fragtNachNamen) nameMerken(name.trim());
      setAbnahmen([abnahme, ...abnahmen]);
      setFreigabeNotiz('');
      toast('success', `Freigegeben — Fassung ${abnahme.rev}.`);
    } catch (fehler) {
      toast('error', fehler instanceof Error ? fehler.message : 'Die Freigabe ging nicht durch.');
    } finally {
      setBusy(false);
    }
  };

  const zurueckziehen = async (a: AbnahmeView) => {
    setBusy(true);
    try {
      const { abnahme } = await api.abnahmeZurueckziehen(a.id);
      setAbnahmen(abnahmen.map((x) => (x.id === abnahme.id ? abnahme : x)));
      toast('info', 'Die Freigabe ist zurückgezogen.');
    } catch (fehler) {
      toast('error', fehler instanceof Error ? fehler.message : 'Das ging nicht.');
    } finally {
      setBusy(false);
    }
  };

  const wurzeln = kommentare.filter((k) => !k.parent_id);
  const offen = wurzeln.filter((k) => !k.resolved_at);
  const erledigt = wurzeln.filter((k) => k.resolved_at);
  const antwortenAuf = (id: string) => kommentare.filter((k) => k.parent_id === id);
  const darfAbhaken = role !== 'viewer';

  /**
   * Aus einer Nachricht zu genau diesem Hinweis (7.276): hinspringen, ihn
   * zeigen -- auch wenn er schon erledigt ist -- und kurz hervorheben. Erst,
   * wenn die Hinweise da sind; ein gelöschter bleibt einfach das Blatt.
   */
  const fokusErledigt = useRef<string | null>(null);
  useEffect(() => {
    if (!fokus || fokusErledigt.current === fokus) return;
    const k = kommentare.find((x) => x.id === fokus);
    if (!k) return;
    fokusErledigt.current = fokus;
    const wurzel = k.parent_id ? (kommentare.find((x) => x.id === k.parent_id) ?? k) : k;
    if (wurzel.resolved_at) setZeigeErledigte(true);
    springe(wurzel);
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        const el = document.querySelector<HTMLElement>(`.sheet [data-kommentar="${CSS.escape(fokus)}"]`);
        el?.scrollIntoView({ block: 'center' });
      }),
    );
    // springe liest nur den aktuellen Schnitt; neu laufen soll es, wenn die Hinweise kommen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fokus, kommentare]);
  // Den Anker erst hier auflösen: wurde der Clip inzwischen gelöscht, ist das
  // Blatt wieder bei der Stelle -- und nicht bei einem Namen, den es nicht
  // mehr gibt.
  const ankerClip = hinweisAnker ? clipFinden(timeline, hinweisAnker) : null;

  const aendern = (k: KommentarView) => {
    setAntwortAuf(null);
    setBearbeite(k);
    setText(k.body);
    // Das Feld steht oben im Blatt; wer unten auf den Stift tippt, soll es
    // nicht suchen müssen.
    requestAnimationFrame(() => {
      const feld = document.querySelector<HTMLTextAreaElement>('.sheet textarea[aria-label="Hinweis"]');
      feld?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      feld?.focus();
    });
  };

  const schreiben = async () => {
    if (!project || !text.trim()) return;
    setBusy(true);
    if (bearbeite) {
      try {
        await api.kommentarAendern(bearbeite.id, text.trim());
        setText('');
        setBearbeite(null);
        await refresh();
      } catch (err) {
        toast('error', err instanceof Error ? err.message : 'Die Änderung ließ sich nicht speichern.');
      } finally {
        setBusy(false);
      }
      return;
    }
    try {
      await api.kommentarSchreiben(project.id, {
        // Eine Antwort gehört an dieselbe Stelle wie die Frage, nicht dorthin,
        // wo der Kopf gerade zufällig steht.
        at: antwortAuf ? antwortAuf.at_seconds : playhead,
        // Eine Antwort erbt den Anker ihrer Frage -- sie handelt von derselben
        // Sache, und zwei Anker in einem Faden wären einer zu viel.
        clipId: antwortAuf ? antwortAuf.clip_id : hinweisAnker,
        text: text.trim(),
        parentId: antwortAuf?.id ?? null,
        name: name.trim(),
      });
      if (fragtNachNamen) nameMerken(name.trim());
      setText('');
      setAntwortAuf(null);
      setHinweisAnker(null);
      await refresh();
    } catch (err) {
      toast('error', err instanceof Error ? err.message : 'Der Kommentar ließ sich nicht speichern.');
    } finally {
      setBusy(false);
    }
  };

  const abhaken = async (k: KommentarView) => {
    try {
      await api.kommentarErledigt(k.id, !k.resolved_at);
      await refresh();
    } catch (err) {
      toast('error', err instanceof Error ? err.message : 'Das ließ sich nicht ändern.');
    }
  };

  /**
   * Löschen fragt einmal nach (7.279): der erste Tipp macht aus dem
   * Papierkorb „Löschen?“, erst der zweite löscht. Ein Tipp daneben am
   * Telefon kostete sonst einen Hinweis -- und seit 7.273 haben auch Gäste
   * den Papierkorb. Nach vier Sekunden ist die Frage wieder weg.
   */
  const [loeschFrage, setLoeschFrage] = useState<string | null>(null);
  useEffect(() => {
    if (!loeschFrage) return;
    const weg = window.setTimeout(() => setLoeschFrage(null), 4000);
    return () => window.clearTimeout(weg);
  }, [loeschFrage]);

  const loeschen = async (k: KommentarView) => {
    setLoeschFrage(null);
    try {
      await api.kommentarLoeschen(k.id);
      await refresh();
    } catch (err) {
      toast('error', err instanceof Error ? err.message : 'Das ließ sich nicht löschen.');
    }
  };

  /**
   * Wohin ein Hinweis springt -- und was an ihm steht.
   *
   * Klebt er an einem Clip, der noch da ist, steht dessen Name auf der Marke
   * und ein Tipp wählt ihn *und* springt hin. Ist der Clip weg, bleibt die
   * Sekunde und die Marke sagt es: der Hinweis ist nicht verloren, sein Objekt
   * ist es.
   */
  const springe = (k: KommentarView) => {
    const ziel = hinweisZiel(timeline, { clipId: k.clip_id, at: k.at_seconds });
    setPlayhead(ziel.at);
    if (ziel.clipId) select([ziel.clipId]);
  };

  const Eintrag = ({ k, antwort = false }: { k: KommentarView; antwort?: boolean }): ReactElement => {
    const ziel = hinweisZiel(timeline, { clipId: k.clip_id, at: k.at_seconds });
    return (
    <div
      className={`kommentar ${antwort ? 'antwort' : ''} ${k.resolved_at ? 'erledigt' : ''} ${k.id === fokus ? 'fokus' : ''}`
        .replace(/\s+/g, ' ')
        .trim()}
      data-kommentar={k.id}
      data-fokus={k.id === fokus ? '' : undefined}
    >
      <div className="kopf">
        {!antwort ? (
          <button
            className={`marke ${ziel.clipId ? 'am-objekt' : ''}`.trim()}
            onClick={() => springe(k)}
            data-am-clip={ziel.clipId ?? undefined}
            data-verwaist={ziel.verwaist ? 'ja' : undefined}
            title={
              ziel.clipId
                ? `Zu „${ziel.label}“ springen`
                : ziel.verwaist
                  ? 'Der Clip dazu ist nicht mehr da — es bleibt die Stelle'
                  : 'Zu dieser Stelle springen'
            }
          >
            {ziel.clipId ? ziel.label : zeit(k.at_seconds)}
            {ziel.verwaist ? ' · weg' : null}
          </button>
        ) : null}
        <span className="wer">
          {k.author_name}
          {k.edited_at ? (
            <span className="geaendert" data-geaendert title={`Geändert ${vorZeit(k.edited_at)}`}>
              {' '}
              · geändert
            </span>
          ) : null}
        </span>
        <span className="spacer" />
        {!antwort && darfAbhaken ? (
          <button className="ghost small" onClick={() => void abhaken(k)} data-erledigt>
            {k.resolved_at ? 'Wieder offen' : 'Erledigt'}
          </button>
        ) : null}
        {/* Nur, wo es auch geht (7.273): vorher stand der Papierkorb an jedem
            Hinweis, und ein Tipp auf einen fremden -- oder als Gast auf den
            eigenen -- endete in einer Fehlermeldung. */}
        {k.darf_aendern ? (
          <button className="ghost small" onClick={() => aendern(k)} aria-label="Hinweis ändern" data-aendern>
            <Icon name="stift" size={14} />
          </button>
        ) : null}
        {k.darf_loeschen ? (
          loeschFrage === k.id ? (
            <button className="ghost small danger" onClick={() => void loeschen(k)} data-loeschen-bestaetigen>
              Löschen?
            </button>
          ) : (
            <button className="ghost small" onClick={() => setLoeschFrage(k.id)} aria-label="Kommentar löschen" data-loeschen>
              <Icon name="trash" size={14} />
            </button>
          )
        ) : null}
      </div>
      {k.deleted_at ? (
        // Gelöscht, aber nicht weg: darunter stehen Antworten anderer (7.278).
        <p className="text geloescht" data-geloescht>
          Hinweis gelöscht
        </p>
      ) : (
        <p className="text">{k.body}</p>
      )}
      {!antwort ? (
        <>
          {antwortenAuf(k.id).map((a) => (
            <Eintrag key={a.id} k={a} antwort />
          ))}
          <button
            className="ghost small antworten"
            onClick={() => {
              setBearbeite(null);
              setAntwortAuf(k);
            }}
          >
            Antworten
          </button>
        </>
      ) : null}
    </div>
    );
  };

  return (
    <Sheet title="Hinweise" onClose={onClose}>
      <div className="field">
        <div className="spread">
          <span data-hinweis-ziel={ankerClip ? ankerClip.clip.id : undefined}>
            {bearbeite
              ? 'Deinen Hinweis ändern'
              : antwortAuf
              ? `Antwort auf ${antwortAuf.author_name}`
              : ankerClip
                ? `Neuer Hinweis an „${ankerClip.clip.label || ankerClip.clip.id}“`
                : `Neuer Hinweis bei ${zeit(playhead)}`}
          </span>
          {bearbeite ? (
            <button
              className="ghost small"
              data-aendern-abbrechen
              onClick={() => {
                setBearbeite(null);
                setText('');
              }}
            >
              Doch nicht
            </button>
          ) : antwortAuf ? (
            <button className="ghost small" onClick={() => setAntwortAuf(null)}>
              Doch nicht
            </button>
          ) : ankerClip ? (
            // Der Anker lässt sich lösen: manchmal meint man doch die Stelle
            // und nicht das Objekt, und dann darf man nicht das Blatt schliessen
            // müssen, um das zu sagen.
            <button className="ghost small" onClick={() => setHinweisAnker(null)} data-anker-loesen>
              Nur die Stelle
            </button>
          ) : null}
        </div>
        <textarea
          value={text}
          rows={3}
          maxLength={2000}
          placeholder={antwortAuf ? 'Deine Antwort…' : 'Was fällt dir hier auf?'}
          onChange={(e) => setText(e.target.value)}
          aria-label="Hinweis"
        />
        {/* Nach dem Namen wird nur gefragt, wenn keiner am Link steht. Steht
            einer, gilt der -- ein Feld, dessen Inhalt der Server verwirft,
            wäre eine Lüge (7.63). */}
        {fragtNachNamen ? (
          <input
            value={name}
            maxLength={80}
            placeholder="Dein Name"
            onChange={(e) => setName(e.target.value)}
            aria-label="Name"
          />
        ) : null}
        {gastName ? <p className="hint" data-gastname-hinweis>Du schreibst als {gastName}.</p> : null}
        <button className="primary" style={{ width: '100%' }} disabled={busy || !text.trim()} onClick={() => void schreiben()}>
          {bearbeite ? 'Speichern' : antwortAuf ? 'Antworten' : 'Hinweis setzen'}
        </button>
      </div>

      <h3 style={{ fontSize: 13, color: 'var(--muted)', margin: '18px 0 8px' }}>
        Offen{offen.length ? ` (${offen.length})` : ''}
      </h3>
      {/* Ein Fehlschlag beim Abholen sieht sonst aus wie „es ist nichts da“ --
          und das ist der Unterschied zwischen einer stillen Leitung und
          verlorenen Hinweisen. */}
      {kommentareFehler ? (
        <div className="fehler-zeile" data-hinweise-fehler>
          <span>Die Hinweise konnten nicht geladen werden. Sie sind nicht weg — nur nicht hier.</span>
          <button className="ghost small" data-hinweise-nochmal onClick={() => void refresh()}>
            Nochmal versuchen
          </button>
        </div>
      ) : offen.length ? (
        offen.map((k) => <Eintrag key={k.id} k={k} />)
      ) : (
        <p className="hint" style={{ marginTop: 0 }}>Nichts offen. Tippe auf die Zeitleiste für einen Hinweis.</p>
      )}

      {erledigt.length ? (
        <>
          <button className="ghost small" style={{ marginTop: 16 }} onClick={() => setZeigeErledigte(!zeigeErledigte)}>
            {zeigeErledigte ? 'Erledigte ausblenden' : `Erledigte zeigen (${erledigt.length})`}
          </button>
          {zeigeErledigte ? erledigt.map((k) => <Eintrag key={k.id} k={k} />) : null}
        </>
      ) : null}

      {/* Der Haken unter den Hinweisen: „von mir aus kann das so raus.“ Er
          hängt an der Fassung, die freigegeben wurde -- wird danach
          weitergeschnitten, steht die Abnahme immer noch da und sagt, worauf
          sie sich bezog. Freigeben darf auch, wer über einen Gast-Link kommt:
          genau dafür ist der Link da. */}
      <h3 style={{ fontSize: 13, color: 'var(--muted)', margin: '24px 0 8px' }}>Abnahme</h3>
      {!abnahmenDa ? (
        <p className="hint laedt" style={{ marginTop: 0 }}>Einen Moment…</p>
      ) : gueltig ? (
        <div className="abnahme" data-abnahme>
          <strong>
            <Icon name="check" size={16} /> Freigegeben von {gueltig.by_name}
          </strong>
          <span className="wann">
            {vorZeit(gueltig.created_at)} · Fassung {gueltig.rev}
          </span>
          {gueltig.note ? <p className="text">{gueltig.note}</p> : null}
          {seither > 0 ? (
            <p className="hint" data-seither>
              Seither weitergeschnitten — jetzt Fassung {rev}.
            </p>
          ) : (
            <p className="hint">Unverändert seither.</p>
          )}
          <button className="ghost small" disabled={busy} onClick={() => void zurueckziehen(gueltig)}>
            Freigabe zurückziehen
          </button>
        </div>
      ) : (
        <p className="hint" style={{ marginTop: 0 }}>
          Noch nicht freigegeben.
        </p>
      )}
      {abnahmenDa && !gueltig ? (
        <div className="field">
          <textarea
            value={freigabeNotiz}
            rows={2}
            maxLength={2000}
            placeholder="Notiz, optional"
            onChange={(e) => setFreigabeNotiz(e.target.value)}
            aria-label="Notiz zur Freigabe"
          />
          <button className="primary" style={{ width: '100%' }} disabled={busy} data-freigeben onClick={() => void freigeben()}>
            Fassung {rev} freigeben
          </button>
        </div>
      ) : null}
      {abnahmen.filter((a) => a.withdrawn_at).length ? (
        <p className="hint">
          {abnahmen.filter((a) => a.withdrawn_at).length === 1
            ? 'Eine frühere Freigabe wurde zurückgezogen.'
            : `${abnahmen.filter((a) => a.withdrawn_at).length} frühere Freigaben wurden zurückgezogen.`}
        </p>
      ) : null}
    </Sheet>
  );
}
